import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, ROLE_BADGE } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard, ArrowLeftRight, FileBarChart, Wallet, Users,
  ReceiptText, LogOut, Moon, Sun, Menu, X, Truck, Contact, HandCoins, Settings, BellRing, Gift, History, TrendingDown, UserCog, UserCircle2, Database, Building2, ShieldAlert, Bell, CalendarCheck, CalendarRange,
} from "lucide-react";
import { DragonIcon } from "./DragonIcon";

const NAV_ICON_COLORS = {
  "/dashboard": "text-sky-400",
  "/transactions": "text-emerald-400",
  "/deliveries": "text-amber-400",
  "/customer-bills": "text-rose-400",
  "/bills": "text-orange-400",
  "/deposits": "text-yellow-300",
  "/cashback": "text-pink-400",
  "/expenses": "text-red-400",
  "/cash-bank": "text-cyan-300",
  "/reports": "text-indigo-300",
  "/admin-reports": "text-violet-300",
  "/reminders": "text-amber-300",
  "/kasbon": "text-lime-300",
  "/cashback/disbursements": "text-fuchsia-300",
  "/users": "text-blue-300",
  "/customers": "text-teal-300",
  "/suppliers": "text-orange-300",
  "/deletion-approvals": "text-rose-400",
  "/settings/supplier-tempo": "text-amber-400",
  "/settings/data": "text-red-300",
  "/portal": "text-emerald-300",
  "/profile": "text-[#C9A227]",
  "/attendance": "text-green-400",
  "/attendance/dashboard": "text-teal-400",
  "/attendance/schedule": "text-cyan-400",
  "/attendance/requests": "text-emerald-400",
  "/attendance/settings": "text-amber-400",
};


const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["owner", "admin"] },
  { to: "/transactions", label: "Order", icon: ArrowLeftRight, roles: ["owner", "admin", "karyawan"], kperm: "can_create_order" },
  { to: "/deliveries", label: "Order Belum Dikirim", icon: Truck, roles: ["owner", "admin", "karyawan"], kperm: "can_create_order" },
  { to: "/deletion-approvals", label: "Persetujuan Hapus", icon: ShieldAlert, roles: ["owner", "admin"], perm: "can_approve_deletion" },
  { to: "/customer-bills", label: "Tagihan Pelanggan", icon: ReceiptText, roles: ["owner", "admin", "karyawan"], perm: "can_manage_billing" },
  { to: "/bills", label: "Tagihan Vendor", icon: ReceiptText, roles: ["owner", "admin"], perm: "can_pay_supplier" },
  { to: "/deposits", label: "Titipan Pelanggan", icon: DragonIcon, roles: ["owner", "admin"] },
  { to: "/cashback", label: "Cashback", icon: Gift, roles: ["owner", "admin"] },
  { to: "/expenses", label: "Pengeluaran", icon: TrendingDown, roles: ["owner", "admin"] },
  { to: "/cash-bank", label: "Kas & Bank", icon: Wallet, roles: ["owner", "admin"], perm: "can_view_cash_bank" },
  { to: "/reports", label: "Laporan Laba Rugi", icon: FileBarChart, roles: ["owner", "admin"] },
  { to: "/admin-reports", label: "Laporan Admin", icon: UserCog, roles: ["owner", "admin"] },
  { to: "/attendance", label: "Absensi", icon: CalendarCheck, roles: ["owner", "admin", "karyawan"] },
  { to: "/attendance/dashboard", label: "Dashboard Absensi", icon: CalendarRange, roles: ["owner"] },
  { to: "/attendance/schedule", label: "Jadwal & Libur", icon: CalendarRange, roles: ["owner"] },
  { to: "/attendance/requests", label: "Persetujuan Absensi", icon: CalendarCheck, roles: ["owner"] },
  { to: "/reminders", label: "Pengingat Jatuh Tempo", icon: BellRing, roles: ["owner", "admin"] },
  { to: "/kasbon", label: "Kasbon Karyawan", icon: HandCoins, roles: ["owner", "admin"] },
  { to: "/cashback/disbursements", label: "Riwayat Pencairan", icon: History, roles: ["owner", "admin"] },
  { to: "/users", label: "Manajemen Pengguna", icon: Users, roles: ["owner"] },
  { to: "/customers", label: "Data Pelanggan", icon: Contact, roles: ["owner", "admin"] },
  { to: "/suppliers", label: "Data Vendor", icon: Building2, roles: ["owner"] },
  { to: "/settings/supplier-tempo", label: "Pengaturan Tempo Supplier", icon: Settings, roles: ["owner"] },
  { to: "/attendance/settings", label: "Pengaturan Absensi", icon: Settings, roles: ["owner"] },
  { to: "/settings/data", label: "Hapus Data", icon: Database, roles: ["owner", "admin"], perm: "can_delete_data" },
  { to: "/portal", label: "Portal Tagihan Saya", icon: ReceiptText, roles: ["supplier"] },
  { to: "/profile", label: "Profil Saya", icon: UserCircle2, roles: ["owner", "admin", "karyawan", "supplier"] },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const [open, setOpen] = useState(false);
  const [pendingDel, setPendingDel] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [notifUnread, setNotifUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);

  const loadNotifs = () => api.get("/notifications").then((r) => { setNotifs(r.data.items || []); setNotifUnread(r.data.unread || 0); }).catch(() => {});
  useEffect(() => {
    const fetchN = () => api.get("/notifications/unread-count").then((r) => setNotifUnread(r.data.count || 0)).catch(() => {});
    fetchN();
    const t = setInterval(fetchN, 30000);
    return () => clearInterval(t);
  }, []);
  const toggleNotif = () => { const n = !notifOpen; setNotifOpen(n); if (n) loadNotifs(); };
  const openNotif = async (it) => {
    try { await api.post(`/notifications/${it.id}/read`); } catch (e) { /* ignore */ }
    setNotifOpen(false); setNotifUnread((u) => Math.max(u - (it.read ? 0 : 1), 0));
    if (it.link) navigate(it.link);
  };
  const markAllRead = async () => { try { await api.post("/notifications/read-all"); } catch (e) { /* ignore */ } setNotifUnread(0); loadNotifs(); };

  const isApprover = user.role === "owner" || user.can_approve_deletion;
  useEffect(() => {
    if (!isApprover) return;
    const fetchCount = () => Promise.all([
      api.get("/deletion-requests/pending-count").then((r) => r.data.count).catch(() => 0),
      api.get("/cancellation-requests/pending-count").then((r) => r.data.count).catch(() => 0),
    ]).then(([a, b]) => setPendingDel((a || 0) + (b || 0)));
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [isApprover]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const items = NAV.filter((n) => n.roles.includes(user.role) && (!n.perm || user.role === "owner" || user[n.perm]) && (!n.kperm || user.role !== "karyawan" || user.can_full_access || user[n.kperm]));

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col bg-[#0A1633] text-white">
      <div className="flex h-20 items-center justify-center border-b border-white/10 px-3">
        <img
          src="https://customer-assets-jai6qajn.emergentagent.net/job_business-ledger-540/artifacts/331de9535150b523_WhatsApp%20Image%202026-10-11%20at%2013.30.27.webp"
          alt="Lala Corporation Indonesia"
          className="h-16 w-full object-contain"
          data-testid="brand-logo"
        />
      </div>
      <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.to.replace("/", "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors border-l-2 ${
                isActive
                  ? "bg-blue-600/90 text-white shadow-lg shadow-blue-950/50 border-[#C9A227]"
                  : "text-blue-100/70 hover:bg-white/10 hover:text-white border-transparent"
              }`
            }
          >
            <item.icon className={`h-[18px] w-[18px] ${NAV_ICON_COLORS[item.to] || "text-[#C9A227]"}`} />
            <span className="flex-1">{item.label}</span>
            {item.to === "/deletion-approvals" && pendingDel > 0 && (
              <span data-testid="nav-del-badge" className="ml-auto grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold">{pendingDel}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 rounded-lg px-3 py-2">
          <div className="grid place-items-center h-9 w-9 rounded-full bg-blue-500/30 border border-[#C9A227]/40 text-sm font-semibold uppercase text-white">
            {user.name?.charAt(0)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate text-white">{user.name}</div>
            <Badge variant="outline" className={`mt-0.5 text-[10px] px-1.5 py-0 ${ROLE_BADGE[user.role]}`}>
              {ROLE_LABELS[user.role]}
            </Badge>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full justify-start gap-3 mt-1 text-blue-100/60 hover:text-white hover:bg-white/10"
        >
          <LogOut className="h-[18px] w-[18px]" /> Keluar
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-white/10 bg-[#0A1633] fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-[#0A1633] border-r border-white/10">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col lg:pl-64 min-w-0">
        <header className="h-14 shrink-0 border-b border-border bg-white/70 dark:bg-[#0d1b3a]/70 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-3 sm:px-5">
          <div className="flex items-center gap-2.5">
            <Button variant="ghost" size="icon" className="lg:hidden h-9 w-9" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            <div>
              <h1 className="font-heading font-bold text-sm sm:text-base leading-tight" data-testid="header-greeting">
                Selamat datang, {user.name} 👋
              </h1>
              <p className="text-[10px] text-muted-foreground leading-tight capitalize" data-testid="header-role">
                {user.stored_role ? "Karyawan · Akses Penuh" : user.role}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Button variant="outline" size="icon" onClick={toggleNotif} data-testid="notif-bell" className="rounded-full h-9 w-9 relative">
                <Bell className="h-[18px] w-[18px]" />
                {notifUnread > 0 && (
                  <span data-testid="notif-badge" className="absolute -top-1 -right-1 grid place-items-center min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold">{notifUnread}</span>
                )}
              </Button>
              {notifOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setNotifOpen(false)} />
                  <div data-testid="notif-panel" className="absolute right-0 mt-2 w-80 max-w-[90vw] z-40 rounded-xl border border-border bg-popover shadow-xl overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                      <span className="text-sm font-semibold">Notifikasi</span>
                      {notifs.length > 0 && <button onClick={markAllRead} data-testid="notif-mark-all" className="text-[11px] text-primary hover:underline">Tandai semua dibaca</button>}
                    </div>
                    <div className="max-h-96 overflow-y-auto divide-y divide-border">
                      {notifs.length === 0 && <p className="text-center text-xs text-muted-foreground py-8" data-testid="notif-empty">Belum ada notifikasi.</p>}
                      {notifs.map((it) => (
                        <button key={it.id} onClick={() => openNotif(it)} data-testid={`notif-item-${it.id}`}
                          className={`block w-full text-left px-3 py-2.5 hover:bg-accent transition-colors ${it.read ? "" : "bg-primary/5"}`}>
                          <div className="flex items-center gap-1.5">
                            {!it.read && <span className="h-1.5 w-1.5 rounded-full bg-rose-500 shrink-0" />}
                            <span className="text-xs font-semibold truncate">{it.title}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{it.message}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <Button variant="outline" size="icon" onClick={() => setDark(!dark)} data-testid="theme-toggle" className="rounded-full h-9 w-9">
              {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 px-3 sm:px-5 lg:px-6 py-4 sm:py-5 min-w-0">{children}</main>
      </div>
    </div>
  );
};
